import React, { useEffect, useMemo, useRef, useState } from "react";
import { Pencil, Plus, RefreshCw, Fuel, AlertCircle, CalendarDays, Loader2, X, Check, Smartphone, Info, Droplet } from "lucide-react";
import Sidebar1 from "components/Sidebar1";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { fetchWithToken, updateWithToken } from "api/ApiHandler";
import { useDialog, DialogFrame } from "../Admindashboard/ui";
import { FONT_HEADING, handleAuthError, EmptyState } from "../userManagement/shared";

const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
const CURRENT_MONTH = MONTHS[new Date().getMonth()];
const CURRENT_YEAR = String(new Date().getFullYear());

const DEFAULT_COLORS = {
  super98Color: "#0897D3",
  special95Color: "#00953B",
  eplus91Color: "#E2211C",
  dieselColor: "#FEA30C",
};

const FUEL_FIELDS = [
  { key: "super98", colorKey: "super98Color", label: "Super 98" },
  { key: "special95", colorKey: "special95Color", label: "Special 95" },
  { key: "eplus91", colorKey: "eplus91Color", label: "E Plus 91" },
  { key: "diesel", colorKey: "dieselColor", label: "Diesel" },
];

const normalizeFuelItem = (item) => ({
  id: item?.sourceId || item?._id || item?.id || null,
  name: item?.name || "",
  nameAr: item?.nameAr || "",
  price: item?.price != null ? String(item.price) : "",
  month: item?.month || "",
  year: item?.year || "",
  color: item?.color || "#000000",
});

const getRowKey = (item) => `${item?.id || ""}-${item?.name || ""}`;
const getFuelField = (name) => FUEL_FIELDS.find((f) => f.label.toUpperCase() === String(name || "").toUpperCase());
const getFuelByField = (list, fieldKey) => list.find((item) => getFuelField(item.name)?.key === fieldKey);

// The API always expects the full set of 4 prices + colours for a month.
const buildFullFuelPayload = (list, overrides = {}) => {
  const first = list[0] || {};
  return {
    month: String(overrides.month || first.month || CURRENT_MONTH).trim(),
    year: String(overrides.year || first.year || CURRENT_YEAR).trim(),
    super98: String(overrides.super98 ?? getFuelByField(list, "super98")?.price ?? "").trim(),
    special95: String(overrides.special95 ?? getFuelByField(list, "special95")?.price ?? "").trim(),
    eplus91: String(overrides.eplus91 ?? getFuelByField(list, "eplus91")?.price ?? "").trim(),
    diesel: String(overrides.diesel ?? getFuelByField(list, "diesel")?.price ?? "").trim(),
    super98Color: String(overrides.super98Color ?? getFuelByField(list, "super98")?.color ?? DEFAULT_COLORS.super98Color).trim(),
    special95Color: String(overrides.special95Color ?? getFuelByField(list, "special95")?.color ?? DEFAULT_COLORS.special95Color).trim(),
    eplus91Color: String(overrides.eplus91Color ?? getFuelByField(list, "eplus91")?.color ?? DEFAULT_COLORS.eplus91Color).trim(),
    dieselColor: String(overrides.dieselColor ?? getFuelByField(list, "diesel")?.color ?? DEFAULT_COLORS.dieselColor).trim(),
  };
};

const isValidPrice = (v) => /^\d{1,3}(\.\d{1,2})?$/.test(String(v).trim()) && Number(v) > 0;
const isValidHex = (v) => /^#[0-9a-f]{6}$/i.test(String(v).trim());
const formatPrice = (v) => (v === "" || v == null || Number.isNaN(Number(v)) ? "—" : Number(v).toFixed(2));

// Plain inputs have no `type`: styles/tailwind.css resets padding/border on typed inputs.
const inputCls =
  "w-full bg-[#F7F7F9] border border-[#EEEEF1] rounded-xl px-3.5 py-3 text-sm text-[#111] placeholder-[#9CA3AF] focus:outline-none focus:border-[#EA352B] focus:bg-[#fff] focus:ring-0 transition";

export default function AdminFuelPricesPage() {
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [fuelList, setFuelList] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [fetchError, setFetchError] = useState("");
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false);

  const loadFuelPrices = async () => {
    try {
      setIsLoading(true);
      setFetchError("");
      const response = await fetchWithToken("/currentFuel/getFuelPrice");
      setFuelList(Array.isArray(response?.getPrice) ? response.getPrice.map(normalizeFuelItem) : []);
    } catch (error) {
      console.error("Failed to load fuel prices:", error);
      if (handleAuthError(error)) return;
      setFetchError("Unable to load current fuel prices.");
      setFuelList([]);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadFuelPrices();
  }, []);

  // Show fuels in the standard order: Super 98, Special 95, E Plus 91, Diesel, then anything else
  const sortedFuels = useMemo(() => {
    const order = (item) => {
      const i = FUEL_FIELDS.findIndex((f) => f.key === getFuelField(item.name)?.key);
      return i === -1 ? 99 : i;
    };
    return [...fuelList].sort((a, b) => order(a) - order(b));
  }, [fuelList]);

  const period = fuelList[0] ? `${fuelList[0].month} ${fuelList[0].year}`.trim() : "";
  const isCurrentPeriod =
    fuelList[0] &&
    String(fuelList[0].month).toLowerCase() === CURRENT_MONTH.toLowerCase() &&
    String(fuelList[0].year) === CURRENT_YEAR;

  return (
    <div className="flex h-screen bg-[#F6F6F8] overflow-hidden">
      <Sidebar1 isOpen={isSidebarOpen} setIsOpen={setIsSidebarOpen} />
      <ToastContainer position="top-right" autoClose={2500} hideProgressBar />

      <main className="flex-1 min-w-0 overflow-y-auto px-8 py-6 lg:px-6 md:px-4 sm:px-3">
        {/* Header */}
        <header className="flex items-center justify-between gap-4 mb-6 md:flex-col md:items-start">
          <div>
            <h1 className="m-0 text-3xl sm:text-2xl font-medium text-[#111]" style={FONT_HEADING}>Fuel Prices</h1>
            <p className="m-0 mt-1 text-sm text-[#6B7280]">The UAE fuel prices customers see in the Fuelingo app.</p>
          </div>
          <div className="flex gap-2 md:w-full">
            <button
              type="button"
              onClick={loadFuelPrices}
              disabled={isLoading}
              className="inline-flex items-center gap-2 rounded-xl bg-[#fff] border border-[#ECECEF] px-4 py-2.5 text-sm font-medium text-[#374151] hover:border-[#EA352B] hover:text-[#EA352B] transition disabled:opacity-60"
            >
              <RefreshCw size={15} className={isLoading ? "animate-spin" : ""} /> Refresh
            </button>
            <button
              type="button"
              onClick={() => setCreating(true)}
              disabled={isLoading}
              className="inline-flex items-center gap-2 rounded-xl bg-[#EA352B] px-4 py-2.5 text-sm font-semibold text-[#fff] hover:bg-[#D42F26] shadow-[0_6px_16px_rgba(234,53,43,0.25)] transition disabled:opacity-60 md:flex-1 md:justify-center"
            >
              <Plus size={16} /> New month prices
            </button>
          </div>
        </header>

        {/* Period banner */}
        <section
          className="relative overflow-hidden rounded-3xl p-6 sm:p-5 mb-6 text-[#fff]"
          style={{ background: "linear-gradient(120deg, #1B1B22 0%, #2A1614 55%, #EA352B 135%)" }}
        >
          <div className="pointer-events-none absolute -right-14 -top-20 w-64 h-64 rounded-full bg-[#EA352B] opacity-20" />
          <Fuel size={140} className="pointer-events-none absolute right-8 -bottom-6 text-[#ffffff0d]" />
          <div className="relative flex items-center justify-between gap-4 flex-wrap">
            <div className="flex items-center gap-4">
              <span className="w-12 h-12 rounded-2xl bg-[#ffffff1f] flex items-center justify-center shrink-0">
                <CalendarDays size={22} />
              </span>
              <div>
                <p className="m-0 text-xs uppercase tracking-wider text-[#ffffff99]">Prices live in the app</p>
                {isLoading ? (
                  <div className="h-7 w-40 mt-1 rounded bg-[#ffffff1f] animate-pulse" />
                ) : (
                  <p className="m-0 mt-0.5 text-2xl font-medium" style={FONT_HEADING}>{period || "Not set yet"}</p>
                )}
              </div>
            </div>
            {!isLoading && fuelList.length > 0 && (
              isCurrentPeriod ? (
                <span className="inline-flex items-center gap-1.5 rounded-full bg-[#DCFCE7] text-[#15803D] px-3 py-1.5 text-xs font-semibold">
                  <Check size={13} /> Up to date for {CURRENT_MONTH}
                </span>
              ) : (
                <button
                  type="button"
                  onClick={() => setCreating(true)}
                  className="inline-flex items-center gap-1.5 rounded-full bg-[#FEF3C7] text-[#92400E] px-3 py-1.5 text-xs font-semibold hover:bg-[#FDE68A] transition"
                >
                  <AlertCircle size={13} /> {CURRENT_MONTH} {CURRENT_YEAR} prices not added yet — add now
                </button>
              )
            )}
          </div>
        </section>

        {/* Price board */}
        {fetchError ? (
          <div className="bg-[#fff] rounded-3xl border border-[#EEEEF1]">
            <EmptyState
              Icon={AlertCircle}
              title={fetchError}
              text="Check your connection and try again."
              action={<button type="button" onClick={loadFuelPrices} className="mt-3 rounded-xl bg-[#EA352B] text-[#fff] px-4 py-2 text-sm font-medium">Try again</button>}
            />
          </div>
        ) : isLoading ? (
          <section className="grid grid-cols-4 lg:grid-cols-2 sm:grid-cols-1 gap-5 md:gap-4 mb-6">
            {Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="h-[196px] rounded-3xl bg-[#fff] border border-[#EEEEF1] animate-pulse" />
            ))}
          </section>
        ) : fuelList.length === 0 ? (
          <div className="bg-[#fff] rounded-3xl border border-[#EEEEF1] mb-6">
            <EmptyState
              Icon={Fuel}
              title="No fuel prices yet"
              text="Add this month’s prices so customers can see them in the app."
              action={
                <button type="button" onClick={() => setCreating(true)} className="mt-3 inline-flex items-center gap-2 rounded-xl bg-[#EA352B] text-[#fff] px-4 py-2 text-sm font-semibold">
                  <Plus size={15} /> Add prices
                </button>
              }
            />
          </div>
        ) : (
          <section className="grid grid-cols-4 lg:grid-cols-2 sm:grid-cols-1 gap-5 md:gap-4 mb-6">
            {sortedFuels.map((item) => (
              <FuelCard key={getRowKey(item)} item={item} onEdit={() => setEditing(item)} />
            ))}
          </section>
        )}

        {/* Preview + notes */}
        {!isLoading && !fetchError && fuelList.length > 0 && (
          <section className="grid grid-cols-[minmax(0,1fr)_minmax(0,1fr)] md:grid-cols-1 gap-6 md:gap-4">
            <div className="bg-[#fff] rounded-3xl border border-[#EEEEF1] p-6 sm:p-5" style={{ boxShadow: "0 1px 3px rgba(17,17,26,0.04)" }}>
              <div className="flex items-center gap-2 mb-4">
                <span className="w-8 h-8 rounded-lg bg-[#FDECEA] flex items-center justify-center"><Smartphone size={15} className="text-[#EA352B]" /></span>
                <h3 className="m-0 text-base font-medium text-[#111]" style={FONT_HEADING}>How customers see it</h3>
              </div>
              <div className="mx-auto max-w-[340px] rounded-[28px] border-[6px] border-[#1B1B22] bg-[#fff] p-4 shadow-[0_16px_40px_rgba(17,17,26,0.12)]">
                <p className="m-0 text-[11px] text-[#9CA3AF]">Fuel prices · {period}</p>
                <div className="mt-3 space-y-2">
                  {sortedFuels.map((f) => (
                    <div key={getRowKey(f)} className="flex items-center justify-between rounded-xl bg-[#F7F7F9] px-3 py-2.5">
                      <span className="flex items-center gap-2 text-sm font-medium text-[#111]">
                        <span className="w-3 h-3 rounded-full" style={{ background: f.color }} /> {f.name}
                      </span>
                      <span className="text-sm font-semibold text-[#111]">AED {formatPrice(f.price)}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="bg-[#fff] rounded-3xl border border-[#EEEEF1] p-6 sm:p-5" style={{ boxShadow: "0 1px 3px rgba(17,17,26,0.04)" }}>
              <div className="flex items-center gap-2 mb-4">
                <span className="w-8 h-8 rounded-lg bg-[#FDECEA] flex items-center justify-center"><Info size={15} className="text-[#EA352B]" /></span>
                <h3 className="m-0 text-base font-medium text-[#111]" style={FONT_HEADING}>Updating prices</h3>
              </div>
              <ol className="m-0 pl-0 list-none space-y-3">
                {[
                  ["New month", "UAE prices change on the 1st of every month. Use “New month prices” to publish all four prices at once."],
                  ["Fix a price", "Use Edit on a fuel card to correct its price or colour for the current month."],
                  ["Colours", "Each fuel’s colour is used for its dot and chart in the app — keep them distinct."],
                ].map(([title, text], i) => (
                  <li key={title} className="flex gap-3">
                    <span className="w-6 h-6 rounded-full bg-[#1B1B22] text-[#fff] text-xs font-semibold flex items-center justify-center shrink-0">{i + 1}</span>
                    <div>
                      <p className="m-0 text-sm font-semibold text-[#111]">{title}</p>
                      <p className="m-0 text-xs text-[#6B7280] leading-relaxed">{text}</p>
                    </div>
                  </li>
                ))}
              </ol>
            </div>
          </section>
        )}
      </main>

      <EditFuelDialog
        item={editing}
        fuelList={fuelList}
        onClose={() => setEditing(null)}
        onSaved={() => { setEditing(null); loadFuelPrices(); }}
      />
      <NewMonthDialog
        open={creating}
        fuelList={fuelList}
        onClose={() => setCreating(false)}
        onSaved={() => { setCreating(false); loadFuelPrices(); }}
      />
    </div>
  );
}

// ─── Pieces ─────────────────────────────────────────────────────────────────

function FuelCard({ item, onEdit }) {
  const [whole, dec] = formatPrice(item.price).split(".");
  return (
    <article className="group relative bg-[#fff] rounded-3xl border border-[#EEEEF1] overflow-hidden hover:shadow-[0_12px_32px_rgba(17,17,26,0.08)] hover:-translate-y-0.5 transition-all" style={{ boxShadow: "0 1px 3px rgba(17,17,26,0.04)" }}>
      <div className="h-2" style={{ background: item.color }} />
      <div className="p-5">
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <span className="w-11 h-11 rounded-2xl flex items-center justify-center shrink-0" style={{ background: `${item.color}1f` }}>
              <Droplet size={20} style={{ color: item.color }} className="fill-current" />
            </span>
            <div className="min-w-0">
              <p className="m-0 text-base font-semibold text-[#111] truncate">{item.name || "Fuel"}</p>
              {item.nameAr && <p className="m-0 text-xs text-[#9CA3AF] truncate" dir="rtl">{item.nameAr}</p>}
            </div>
          </div>
          <button
            type="button"
            onClick={onEdit}
            aria-label={`Edit ${item.name}`}
            className="shrink-0 inline-flex items-center gap-1.5 rounded-lg border border-[#EEEEF1] px-2.5 py-1.5 text-xs font-semibold text-[#374151] hover:border-[#EA352B] hover:text-[#EA352B] transition"
          >
            <Pencil size={12} /> Edit
          </button>
        </div>

        <div className="mt-5 flex items-end gap-1">
          <span className="text-sm font-semibold text-[#9CA3AF] mb-1.5">AED</span>
          <span className="text-5xl font-semibold text-[#111] leading-none tracking-tight">{whole}</span>
          {dec && <span className="text-2xl font-semibold text-[#111] leading-none mb-0.5">.{dec}</span>}
          <span className="text-xs text-[#9CA3AF] mb-1.5 ml-1">/ litre</span>
        </div>

        <div className="mt-4 flex items-center justify-between text-[11px] text-[#9CA3AF]">
          <span className="inline-flex items-center gap-1"><CalendarDays size={12} /> {item.month} {item.year}</span>
          <span className="inline-flex items-center gap-1.5 font-mono uppercase">
            <span className="w-2.5 h-2.5 rounded-full" style={{ background: item.color }} /> {item.color}
          </span>
        </div>
      </div>
    </article>
  );
}

function ColorField({ value, onChange, label }) {
  return (
    <div className="flex items-center gap-2">
      <label className="relative w-12 h-12 rounded-xl border border-[#EEEEF1] overflow-hidden cursor-pointer shrink-0" style={{ background: isValidHex(value) ? value : "#fff" }} title={`Pick ${label} colour`}>
        <input
          type="color"
          value={isValidHex(value) ? value : "#000000"}
          onChange={(e) => onChange(e.target.value.toUpperCase())}
          className="absolute inset-0 opacity-0 cursor-pointer"
          aria-label={`${label} colour`}
        />
      </label>
      <input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        maxLength={7}
        spellCheck={false}
        aria-label={`${label} colour hex`}
        className={`${inputCls} font-mono uppercase ${value && !isValidHex(value) ? "border-[#EA352B]" : ""}`}
      />
    </div>
  );
}

function PriceField({ value, onChange, label, autoFocus }) {
  const invalid = value !== "" && !isValidPrice(value);
  return (
    <div className="relative">
      <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-xs font-semibold text-[#9CA3AF]">AED</span>
      <input
        inputMode="decimal"
        autoFocus={autoFocus}
        value={value}
        onChange={(e) => onChange(e.target.value.replace(/[^\d.]/g, ""))}
        placeholder="0.00"
        aria-label={`${label} price`}
        aria-invalid={invalid}
        className={`${inputCls} pl-12 pr-14 text-base font-semibold ${invalid ? "border-[#EA352B]" : ""}`}
      />
      <span className="absolute right-3.5 top-1/2 -translate-y-1/2 text-xs text-[#9CA3AF]">/ litre</span>
    </div>
  );
}

function DialogShell({ open, title, subtitle, onClose, busy, children, footer, maxWidth }) {
  const closeRef = useRef(null);
  useDialog(open && !busy, onClose);
  if (!open) return null;
  return (
    <DialogFrame labelledBy="fuel-dialog-title" onClose={busy ? () => {} : onClose} maxWidth={maxWidth}>
      <div className="relative shrink-0 px-6 pt-6 pb-5 sm:px-4 text-[#fff] overflow-hidden" style={{ background: "linear-gradient(120deg, #1B1B22 0%, #2A1614 60%, #EA352B 140%)" }}>
        <div className="pointer-events-none absolute -right-12 -top-16 w-52 h-52 rounded-full bg-[#EA352B] opacity-20" />
        <button
          ref={closeRef}
          type="button"
          aria-label="Close"
          onClick={onClose}
          disabled={busy}
          className="absolute z-10 top-4 right-4 w-9 h-9 rounded-xl bg-[#ffffff1f] hover:bg-[#ffffff33] flex items-center justify-center transition disabled:opacity-50"
        >
          <X size={18} className="text-[#fff]" />
        </button>
        <div className="relative pr-10">
          <h2 id="fuel-dialog-title" className="m-0 text-xl font-medium" style={FONT_HEADING}>{title}</h2>
          {subtitle && <p className="m-0 mt-1 text-xs text-[#ffffffb3]">{subtitle}</p>}
        </div>
      </div>
      <div className="flex-1 min-h-0 overflow-y-auto p-6 sm:p-4 bg-[#fff]">{children}</div>
      <div className="shrink-0 flex gap-3 justify-end px-6 py-4 sm:px-4 bg-[#fff] border-t border-[#EEEEF1]">{footer}</div>
    </DialogFrame>
  );
}

function FooterButtons({ onCancel, onSave, busy, disabled, label }) {
  return (
    <>
      <button type="button" onClick={onCancel} disabled={busy} className="rounded-xl border border-[#EEEEF1] px-5 py-2.5 text-sm font-semibold text-[#374151] hover:bg-[#F7F7F9] transition disabled:opacity-60 sm:flex-1">
        Cancel
      </button>
      <button type="button" onClick={onSave} disabled={busy || disabled} className="inline-flex items-center justify-center gap-2 rounded-xl bg-[#EA352B] px-5 py-2.5 text-sm font-semibold text-[#fff] hover:bg-[#D42F26] transition disabled:opacity-60 sm:flex-1">
        {busy ? <Loader2 size={15} className="animate-spin" /> : <Check size={15} />} {busy ? "Saving…" : label}
      </button>
    </>
  );
}

function EditFuelDialog({ item, fuelList, onClose, onSaved }) {
  const [price, setPrice] = useState("");
  const [color, setColor] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (item) {
      setPrice(item.price);
      setColor(String(item.color || "").toUpperCase());
    }
  }, [item]);

  const valid = isValidPrice(price) && isValidHex(color);
  const changed = item && (String(price) !== String(item.price) || color.toLowerCase() !== String(item.color).toLowerCase());

  const save = () => {
    if (!valid) {
      toast.error("Enter a valid price (e.g. 2.58) and colour (e.g. #0897D3).");
      return;
    }
    setSaving(true);
    const field = getFuelField(item.name);
    updateWithToken("/currentFuel/updateFuelPrice", {
      id: item.id,
      ...buildFullFuelPayload(fuelList, field
        ? { [field.key]: price, [field.colorKey]: color, month: item.month, year: item.year }
        : { month: item.month, year: item.year }),
    })
      .then(() => {
        toast.success(`${item.name} updated to AED ${formatPrice(price)}`);
        onSaved();
      })
      .catch((error) => {
        console.error("Failed to update fuel price:", error);
        if (!handleAuthError(error)) toast.error("Failed to update. Try again.");
      })
      .finally(() => setSaving(false));
  };

  return (
    <DialogShell
      open={!!item}
      title={`Edit ${item?.name || "fuel"}`}
      subtitle={item ? `${item.month} ${item.year} · currently AED ${formatPrice(item.price)}` : ""}
      onClose={onClose}
      busy={saving}
      maxWidth={480}
      footer={<FooterButtons onCancel={onClose} onSave={save} busy={saving} disabled={!valid || !changed} label="Save changes" />}
    >
      {item && (
        <div className="space-y-5">
          <div>
            <p className="m-0 mb-2 text-xs font-semibold text-[#6B7280]">Price</p>
            <PriceField value={price} onChange={setPrice} label={item.name} autoFocus />
            {price !== "" && !isValidPrice(price) && <p className="m-0 mt-1.5 text-[11px] text-[#EA352B]">Use a number like 2.58 (max 2 decimals).</p>}
            {isValidPrice(price) && Number(price) !== Number(item.price) && (
              <p className={`m-0 mt-1.5 text-[11px] font-semibold ${Number(price) > Number(item.price) ? "text-[#EA352B]" : "text-[#16A34A]"}`}>
                {Number(price) > Number(item.price) ? "▲" : "▼"} AED {Math.abs(Number(price) - Number(item.price)).toFixed(2)} vs current
              </p>
            )}
          </div>
          <div>
            <p className="m-0 mb-2 text-xs font-semibold text-[#6B7280]">Colour in the app</p>
            <ColorField value={color} onChange={setColor} label={item.name} />
          </div>
          <div className="rounded-2xl bg-[#F7F7F9] p-4">
            <p className="m-0 mb-2 text-[10px] uppercase tracking-wider text-[#9CA3AF]">Preview</p>
            <div className="flex items-center justify-between rounded-xl bg-[#fff] px-3 py-2.5 border border-[#EEEEF1]">
              <span className="flex items-center gap-2 text-sm font-medium text-[#111]">
                <span className="w-3 h-3 rounded-full" style={{ background: isValidHex(color) ? color : "#E5E7EB" }} /> {item.name}
              </span>
              <span className="text-sm font-semibold text-[#111]">AED {formatPrice(price)}</span>
            </div>
          </div>
        </div>
      )}
    </DialogShell>
  );
}

function NewMonthDialog({ open, fuelList, onClose, onSaved }) {
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  // Start from the current prices so the admin only changes what moved
  useEffect(() => {
    if (!open) return;
    const base = buildFullFuelPayload(fuelList);
    const nextIdx = MONTHS.findIndex((m) => m.toLowerCase() === String(fuelList[0]?.month || "").toLowerCase());
    const alreadyCurrent =
      String(fuelList[0]?.month || "").toLowerCase() === CURRENT_MONTH.toLowerCase() && String(fuelList[0]?.year) === CURRENT_YEAR;
    const month = alreadyCurrent && nextIdx !== -1 ? MONTHS[(nextIdx + 1) % 12] : CURRENT_MONTH;
    const year = alreadyCurrent && nextIdx === 11 ? String(Number(CURRENT_YEAR) + 1) : CURRENT_YEAR;
    setForm({ ...base, month, year });
  }, [open, fuelList]);

  if (!open || !form) return null;

  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));
  const errors = FUEL_FIELDS.filter((f) => !isValidPrice(form[f.key]) || !isValidHex(form[f.colorKey]));
  const yearValid = /^\d{4}$/.test(form.year);
  const valid = errors.length === 0 && yearValid && form.month;

  const save = () => {
    if (!valid) {
      toast.error("Please fill Month, Year, and a valid price + colour for every fuel.");
      return;
    }
    setSaving(true);
    updateWithToken("/currentFuel/createFuelPrice", [buildFullFuelPayload(fuelList, form)])
      .then(() => {
        toast.success(`${form.month} ${form.year} prices published!`);
        onSaved();
      })
      .catch((error) => {
        console.error("Failed to create fuel price:", error);
        if (!handleAuthError(error)) toast.error("Failed to add. Try again.");
      })
      .finally(() => setSaving(false));
  };

  return (
    <DialogShell
      open={open}
      title="New month prices"
      subtitle="Starts from the current prices — change the ones that moved."
      onClose={onClose}
      busy={saving}
      maxWidth={640}
      footer={<FooterButtons onCancel={onClose} onSave={save} busy={saving} disabled={!valid} label="Publish prices" />}
    >
      <div className="space-y-5">
        <div className="grid grid-cols-2 gap-3">
          <div>
            <p className="m-0 mb-2 text-xs font-semibold text-[#6B7280]">Month</p>
            <select
              value={form.month}
              onChange={(e) => set("month")(e.target.value)}
              className="w-full appearance-none bg-[#F7F7F9] border border-[#EEEEF1] rounded-xl px-3.5 py-3 text-sm text-[#111] focus:outline-none focus:border-[#EA352B] focus:ring-0 cursor-pointer"
            >
              {MONTHS.map((m) => <option key={m} value={m}>{m}</option>)}
            </select>
          </div>
          <div>
            <p className="m-0 mb-2 text-xs font-semibold text-[#6B7280]">Year</p>
            <input
              inputMode="numeric"
              value={form.year}
              onChange={(e) => set("year")(e.target.value.replace(/\D/g, "").slice(0, 4))}
              className={`${inputCls} ${yearValid ? "" : "border-[#EA352B]"}`}
            />
          </div>
        </div>

        <div className="space-y-3">
          {FUEL_FIELDS.map((f) => {
            const current = getFuelByField(fuelList, f.key)?.price;
            const diff = isValidPrice(form[f.key]) && current ? Number(form[f.key]) - Number(current) : 0;
            return (
              <div key={f.key} className="rounded-2xl border border-[#EEEEF1] p-4">
                <div className="flex items-center justify-between gap-2 mb-3">
                  <span className="flex items-center gap-2 text-sm font-semibold text-[#111]">
                    <span className="w-3 h-3 rounded-full" style={{ background: isValidHex(form[f.colorKey]) ? form[f.colorKey] : "#E5E7EB" }} /> {f.label}
                  </span>
                  {current && (
                    <span className="text-[11px] text-[#9CA3AF]">
                      now AED {formatPrice(current)}
                      {diff !== 0 && (
                        <b className={`ml-1.5 ${diff > 0 ? "text-[#EA352B]" : "text-[#16A34A]"}`}>
                          {diff > 0 ? "▲" : "▼"} {Math.abs(diff).toFixed(2)}
                        </b>
                      )}
                    </span>
                  )}
                </div>
                <div className="grid grid-cols-[minmax(0,1fr)_200px] sm:grid-cols-1 gap-3">
                  <PriceField value={form[f.key]} onChange={set(f.key)} label={f.label} />
                  <ColorField value={form[f.colorKey]} onChange={set(f.colorKey)} label={f.label} />
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </DialogShell>
  );
}
